import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import { StyledDialog } from 'theme/index';

interface ImagePreviewDialogProps {
  open: boolean;
  images: string[];
  documentName?: string;
  loading?: boolean;
  onClose: () => void;
}

const ImagePreviewDialog: React.FC<ImagePreviewDialogProps> = ({
  open,
  images,
  documentName,
  loading = false,
  onClose,
}) => {
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    if (open) {
      setActiveIndex(0);
    }
  }, [open, images]);

  const hasImages = images.length > 0;
  const canGoPrev = activeIndex > 0;
  const canGoNext = activeIndex < images.length - 1;

  let dialogContent: React.ReactNode;

  if (loading) {
    dialogContent = (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  } else if (hasImages) {
    dialogContent = (
      <Stack spacing={2} alignItems="center">
        <Box
          component="img"
          src={images[activeIndex]}
          alt={`Raw image ${activeIndex + 1}`}
          sx={{
            width: '100%',
            maxHeight: { xs: 320, sm: 480 },
            objectFit: 'contain',
            borderRadius: 1,
            border: '1px solid #F2F4F7',
            bgcolor: '#F9FAFB',
          }}
        />

        <Stack direction="row" alignItems="center" spacing={2}>
          <IconButton
            onClick={() => setActiveIndex((i) => i - 1)}
            disabled={!canGoPrev}
          >
            <ChevronLeftIcon />
          </IconButton>

          <Typography variant="body2">
            {activeIndex + 1} of {images.length}
          </Typography>

          <IconButton
            onClick={() => setActiveIndex((i) => i + 1)}
            disabled={!canGoNext}
          >
            <ChevronRightIcon />
          </IconButton>
        </Stack>
      </Stack>
    );
  } else {
    dialogContent = (
      <Typography color="text.secondary">
        No raw images available for this document.
      </Typography>
    );
  }

  return (
    <StyledDialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 600 }}>
        Document Images
        {documentName ? ` — ${documentName}` : ''}
      </DialogTitle>

      <DialogContent>{dialogContent}</DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button variant="contained" onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </StyledDialog>
  );
};

export default ImagePreviewDialog;
